import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { TEST_IDS } from "@/constants/testIds";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, checkout, totalItems, totalPrice, loading } = useCart();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        data-testid={TEST_IDS.CART.drawer}
        className="w-full sm:max-w-md bg-cream flex flex-col"
      >
        <SheetHeader className="pb-4 border-b border-plum/10">
          <SheetTitle className="font-serif text-xl text-plum flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Your Bag
            {totalItems > 0 && (
              <span className="text-sm font-sans font-medium text-plum-light">
                ({totalItems})
              </span>
            )}
          </SheetTitle>
          <SheetDescription className="text-sm text-plum-light">
            {totalItems === 0
              ? "Your bag is empty. Time to treat yourself."
              : "Review your selections below."}
          </SheetDescription>
        </SheetHeader>

        {/* Cart items */}
        <div className="flex-1 overflow-y-auto py-6 space-y-4">
          <AnimatePresence mode="popLayout">
            {items.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex gap-4"
              >
                {/* Image */}
                <div
                  className="w-20 h-24 rounded-xl overflow-hidden shrink-0"
                  style={{ backgroundColor: item.color || "#FDEAEB" }}
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <div>
                      {item.category && (
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-plum-light">
                          {item.category}
                        </p>
                      )}
                      <h4 className="font-serif text-sm font-semibold text-plum truncate">
                        {item.title}
                      </h4>
                    </div>
                    <button
                      data-testid={TEST_IDS.CART.removeItem(item.id)}
                      onClick={() => removeItem(item.id)}
                      className="p-1 text-plum-light hover:text-plum transition-colors duration-200"
                      aria-label={`Remove ${item.title}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    {/* Quantity */}
                    <div
                      data-testid={TEST_IDS.CART.quantity(item.id)}
                      className="flex items-center gap-2 border border-plum/10 rounded-full px-2 py-1"
                    >
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-0.5 text-plum-light hover:text-plum transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-semibold text-plum w-5 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-0.5 text-plum-light hover:text-plum transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>

                    <span className="text-sm font-semibold text-plum">
                      ${(item.price * item.quantity).toLocaleString()}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {items.length === 0 && (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <ShoppingBag className="w-10 h-10 text-plum/20 mb-4" />
              <p className="text-sm text-plum-light">
                Nothing here yet. Go explore!
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="pt-4 border-t border-plum/10 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-plum-light">
                Subtotal
              </span>
              <span
                data-testid={TEST_IDS.CART.total}
                className="text-lg font-serif font-bold text-plum"
              >
                ${totalPrice.toLocaleString()}
              </span>
            </div>
            <Button
              data-testid={TEST_IDS.CART.checkout}
              onClick={checkout}
              disabled={loading}
              className="w-full rounded-full bg-plum text-white hover:bg-plum/90 py-6 font-sans font-semibold text-base transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Updating..." : "Checkout"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </Button>
            <p className="text-center text-xs text-plum-muted">
              Shipping calculated at checkout
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
